import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api`;
console.log('[GuardianBazaar] API URL:', process.env.REACT_APP_API_URL, '→', API);
const TOKEN_KEY = 'guardianbazaar_token';

export interface AuthUser {
  userId:    number;
  username:  string;
  email:     string;
  role:      string;
  firstName?: string;
  lastName?:  string;
}

export interface UserPrefs {
  emailPriceUpdate:          boolean;
  emailPortfolioReport:      boolean;
  emailRlReport:             boolean;
  emailMarketMovers:         boolean;
  emailSpotlight:            boolean;
  emailDataQuality:          boolean;
  theme:                     string;
  landingPage:               string;
  defaultCollectionFormat:   string;
  defaultCollectionView:     string;
  defaultGridSize:           string;
  showAllPrintingsDefault:   boolean;
  ownedOnlyDefault:          boolean;
  defaultDeckView:           string;
}

const DEFAULT_PREFS: UserPrefs = {
  emailPriceUpdate:          true,
  emailPortfolioReport:      true,
  emailRlReport:             true,
  emailMarketMovers:         true,
  emailSpotlight:            true,
  emailDataQuality:          false,
  theme:                     'dark',
  landingPage:               'collection',
  defaultCollectionFormat:   'standard',
  defaultCollectionView:     'grid',
  defaultGridSize:           'md',
  showAllPrintingsDefault:   false,
  ownedOnlyDefault:          true,
  defaultDeckView:           'cards',
};

interface AuthContextType {
  user:               AuthUser | null;
  token:              string | null;
  loading:            boolean;
  prefs:              UserPrefs;
  login:              (username: string, password: string, rememberMe: boolean) => Promise<void>;
  logout:             () => void;
  refreshPreferences: () => Promise<void>;
  isAdmin:            boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user,    setUser]    = useState<AuthUser | null>(null);
  const [token,   setToken]   = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [prefs,   setPrefs]   = useState<UserPrefs>(DEFAULT_PREFS);

  const setAxiosToken = (t: string | null) => {
    if (t) axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    else   delete axios.defaults.headers.common['Authorization'];
  };

  const fetchPreferences = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/preferences`);
      setPrefs(prev => ({ ...DEFAULT_PREFS, ...prev, ...res.data }));
    } catch {
      // keep defaults
    }
  }, []);

  // Restore session on page load
  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) {
      setToken(stored);
      setAxiosToken(stored);
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${stored}` }
      })
        .then(res => {
          setUser(res.data);
          return axios.get(`${API}/users/preferences`);
        })
        .then(res => {
          setPrefs(prev => ({ ...DEFAULT_PREFS, ...prev, ...res.data }));
        })
        .catch(() => {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setAxiosToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Axios interceptor — auto-logout on 401
  useEffect(() => {
    const id = axios.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401 && token) logout();
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [token]);

  const login = async (username: string, password: string, rememberMe: boolean) => {
    const res = await axios.post(`${API}/auth/login`, { username, password, rememberMe });
    const { token: t, ...userData } = res.data;
    setToken(t);
    setUser(userData);
    setAxiosToken(t);
    localStorage.setItem(TOKEN_KEY, t);
    try {
      const prefsRes = await axios.get(`${API}/users/preferences`);
      setPrefs(prev => ({ ...DEFAULT_PREFS, ...prev, ...prefsRes.data }));
    } catch { /* keep defaults */ }
  };

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setPrefs(DEFAULT_PREFS);
    setAxiosToken(null);
    localStorage.removeItem(TOKEN_KEY);
  }, []);

  const refreshPreferences = useCallback(async () => {
    await fetchPreferences();
  }, [fetchPreferences]);

  return (
    <AuthContext.Provider value={{
      user, token, loading, prefs,
      login, logout, refreshPreferences,
      isAdmin: user?.role === 'ADMIN',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
